/**
 * Life Blueprint V2.9.9 — Migration Batch 07-2 · Sheet27 B85:E88 closure.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE
 *   Sheet27!B85, C85, D85, E85 — top row: day-master × pillar stem → 十神
 *   Sheet27!B86:E86              — middle: chained (branch 本气 → hidden stem
 *                                              → day-master × hidden stem)
 *   Sheet27!B87:E87              — middle: chained via 中气
 *   Sheet27!B88:E88              — middle: chained via 余气
 *
 *   D85 is a DISPLAY_STATIC_CONSTANT (audited literal `"日主"` including the
 *   ASCII double-quote characters). It is NOT a CoreRuleEngine calculation
 *   node — Batch 07-2 consumes the pre-existing constant from Batch 07 and
 *   MUST NOT re-derive it. Only 15 cells are computed by this module.
 *
 * AUTHORITATIVE EVIDENCE (2026-02, workbook SHA-256
 *   6d97148d708a3dbc84bcec9bdce9facdb815891de077036c9fc118a75e86d500)
 *
 *   Exact Excel formulas:
 *     B85 =IFERROR(VLOOKUP($B$52 & "_" & $B$36, 'DB_十神'!$F:$I, 4, FALSE()), "")
 *     C85 =IFERROR(VLOOKUP($B$52 & "_" & $B$45, 'DB_十神'!$F:$I, 4, FALSE()), "")
 *     E85 =IFERROR(VLOOKUP($B$52 & "_" & $B$61, 'DB_十神'!$F:$I, 4, FALSE()), "")
 *
 *     B86 =IFERROR(VLOOKUP($B$52 & "_" &
 *            IFERROR(VLOOKUP($B$37 & "_本气", 'DB_藏干_展开'!$A:$D, 4, FALSE()), ""),
 *            'DB_十神'!$F:$I, 4, FALSE()), "")
 *     (C86/D86/E86 identical structure with $B$46 / $B$54 / $B$62 respectively;
 *      B87..E87 with "_中气"; B88..E88 with "_余气".)
 *
 *   Semantic boundaries (per authoritative Excel Value2 semantics):
 *     - INNER IFERROR: missing hidden-stem key → "".
 *     - OUTER IFERROR: outer VLOOKUP failure (including composite key of
 *       shape "<dayStem>_") → "".
 *     - Both layers resolve to exact empty string "" (JavaScript "", length 0).
 *
 *   The current Golden Master fixture (1977-02-04 16:15) supplies:
 *     Year  pillar: B36=丁 (U+4E01), B37=巳 (U+5DF3)
 *     Month pillar: B45=壬 (U+58EC), B46=寅 (U+5BC5)
 *     Day   pillar: B52=辛 (U+8F9B, day master), B54=卯 (U+536F)
 *     Hour  pillar: B61=丙 (U+4E19), B62=申 (U+7533)
 *   → 15 audited Golden Master output cells (D85 fixed static).
 *
 * ISOLATION
 *   NOT wired into the production `runCoreRuleEngineV299` pipeline (which
 *   still throws NOT_IMPLEMENTED). NOT wired into any React component. Does
 *   NOT modify or duplicate any M02 ReferenceData row — DB_十神 (Batch 03)
 *   and DB_藏干_展开 (Batch 06 compatibility over Batch 02 DB_藏干) are
 *   reused as the SOLE sources of truth. Does NOT modify Batch 07's
 *   partial-audit slice (B85/B86/B87/B88 year-pillar closure remains in
 *   place — Batch 07-2 supersedes those cells only when this composer is
 *   used, and no code path reads both at the same time).
 *
 * FAIL-CLOSED CONTRACT PRESERVATION
 *   The underlying M02 helpers (`shiShenLookupBy`, `zhanKaiLookupBy`) MUST
 *   continue to throw `MISSING_REFERENCE_KEY` for unknown keys — that
 *   contract is preserved and Batch 07-2 does NOT weaken it. The Sheet27
 *   cell-semantic IFERROR wrap is implemented ONLY inside this composer
 *   using the non-throwing existence probe (`zhanKaiHasKey`) for the inner
 *   VLOOKUP and a try/catch that catches ONLY `MISSING_REFERENCE_KEY` (never
 *   TypeError, never any other unexpected failure) for the outer VLOOKUP.
 *
 * UNICODE POLICY
 *   No Chinese literals appear in this file; all glyphs flow through as
 *   audited upstream inputs. Position tokens 本气 / 中气 / 余气 are
 *   consumed via the audited `QI_LEI_TOKEN` from Batch 06.
 *
 * DETERMINISM
 *   No wall clock, no randomness, no LLM. Same audited inputs → byte-wise
 *   identical output.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { shiShenLookupBy } from "@/engine/v299/m02_batch03_reference";
import {
  QI_LEI_TOKEN,
  zhanKaiLookupBy,
  zhanKaiHasKey,
} from "@/engine/v299/m02_batch06_reference";
import { D85_DISPLAY_STATIC_CONSTANT } from "@/engine/v299/batch07_sheet27";

// ── Audited constants ────────────────────────────────────────────────────

/** ASCII underscore U+005F — composite-key separator inside DB_藏干_展开. */
const KEY_SEP = "_";

/** Audited empty-string result of `IFERROR(..., "")`. */
const EMPTY = "";

/**
 * Ordered position tokens used across rows 86/87/88.
 * Each is passed through Batch 06's authoritative `QI_LEI_TOKEN`.
 *
 *   row86 → 本气 (position 1)
 *   row87 → 中气 (position 2)
 *   row88 → 余气 (position 3)
 */
export const POSITION_TOKEN = Object.freeze({
  BEN_QI: QI_LEI_TOKEN.BEN_QI,
  ZHONG_QI: QI_LEI_TOKEN.ZHONG_QI,
  YU_QI: QI_LEI_TOKEN.YU_QI,
});

const ROW_TO_POSITION = Object.freeze({
  row86: POSITION_TOKEN.BEN_QI,
  row87: POSITION_TOKEN.ZHONG_QI,
  row88: POSITION_TOKEN.YU_QI,
});

// ── Type guards ──────────────────────────────────────────────────────────

function requireStringArg(name, v) {
  if (typeof v !== "string" || v.length === 0) {
    throw new TypeError(
      `[Batch07-2] ${name} must be a non-empty string (audited glyph).`
    );
  }
  return v;
}

// ── Sheet27 formula-cell semantic wrappers ──────────────────────────────

/**
 * IFERROR(VLOOKUP(<dayStem>_<targetStem>, 'DB_十神'!F:I, 4, FALSE()), "")
 *
 * Audited failure semantics:
 *   - If the composite key does not exist in DB_十神 → return "".
 *   - The special case `targetStem === ""` (inner hidden-stem miss for
 *     rows 86/87/88) yields the composite key `<dayStem>_` which cannot
 *     match any DB_十神 row (all keys are `<stem>_<stem>` with two stems);
 *     the M02 lookup throws MISSING_REFERENCE_KEY, which this wrapper
 *     translates to "".
 */
function shiShenIfErrorEmpty(dayStem, targetStem) {
  try {
    return shiShenLookupBy(dayStem, targetStem);
  } catch (e) {
    if (e && e.code === "MISSING_REFERENCE_KEY") return EMPTY;
    throw e;
  }
}

/**
 * IFERROR(VLOOKUP(<branch>_<position>, 'DB_藏干_展开'!A:D, 4, FALSE()), "")
 *
 * Uses the non-throwing existence probe from Batch 06 so the M02 strict
 * contract (`MISSING_REFERENCE_KEY` on `zhanKaiLookupBy` miss) remains
 * intact for direct callers.
 */
function hiddenStemIfErrorEmpty(branch, position) {
  const key = branch + KEY_SEP + position;
  if (!zhanKaiHasKey(key)) return EMPTY;
  const row = zhanKaiLookupBy(branch, position);
  return row.cangGan;
}

// ── Row 85: direct day-master × pillar stem lookups ─────────────────────

/**
 * Row 85 formula-cell computer.
 *   =IFERROR(VLOOKUP($B$52 & "_" & <pillarStem>, 'DB_十神'!$F:$I, 4, FALSE()), "")
 *
 * @param {string} dayStem     audited day-master glyph ($B$52)
 * @param {string} pillarStem  audited pillar stem glyph ($B$36 / $B$45 /
 *                             $B$61 for B85 / C85 / E85 respectively)
 * @returns {string} audited 十神 token or "" on outer VLOOKUP failure
 */
export function computeSheet27Row85Cell(dayStem, pillarStem) {
  requireStringArg("dayStem", dayStem);
  requireStringArg("pillarStem", pillarStem);
  return shiShenIfErrorEmpty(dayStem, pillarStem);
}

// ── Rows 86/87/88: chained hidden-stem → 十神 lookups ───────────────────

/**
 * Rows 86/87/88 formula-cell computer.
 *   =IFERROR(VLOOKUP($B$52 & "_" &
 *      IFERROR(VLOOKUP(<branch> & "_" & <position>,
 *              'DB_藏干_展开'!$A:$D, 4, FALSE()), ""),
 *      'DB_十神'!$F:$I, 4, FALSE()), "")
 *
 * @param {string} dayStem   audited day-master glyph ($B$52)
 * @param {string} branch    audited pillar branch glyph
 *                           ($B$37 / $B$46 / $B$54 / $B$62 for
 *                           columns B / C / D / E respectively)
 * @param {string} position  audited position token — one of
 *                           `POSITION_TOKEN.BEN_QI` (row 86),
 *                           `POSITION_TOKEN.ZHONG_QI` (row 87),
 *                           `POSITION_TOKEN.YU_QI` (row 88).
 * @returns {string} audited 十神 token or "" on either IFERROR path
 */
export function computeSheet27Rows86To88Cell(dayStem, branch, position) {
  requireStringArg("dayStem", dayStem);
  requireStringArg("branch", branch);
  requireStringArg("position", position);
  if (
    position !== POSITION_TOKEN.BEN_QI &&
    position !== POSITION_TOKEN.ZHONG_QI &&
    position !== POSITION_TOKEN.YU_QI
  ) {
    throw new TypeError(
      "[Batch07-2] position must be one of the audited 本气/中气/余气 tokens."
    );
  }
  const hiddenStem = hiddenStemIfErrorEmpty(branch, position);
  if (hiddenStem === EMPTY) return EMPTY;
  return shiShenIfErrorEmpty(dayStem, hiddenStem);
}

// ── Full B85:E88 grid ────────────────────────────────────────────────────

/**
 * Compute the entire Sheet27 B85:E88 grid deterministically from the four
 * audited pillar inputs.
 *
 * D85 is the audited static display constant (from Batch 07) and is NOT
 * re-derived here. Every other cell is computed as an isolated IFERROR-
 * wrapped Excel formula.
 *
 * @param {{
 *   dayStem: string,     // $B$52
 *   yearStem: string,    // $B$36
 *   yearBranch: string,  // $B$37
 *   monthStem: string,   // $B$45
 *   monthBranch: string, // $B$46
 *   dayBranch: string,   // $B$54
 *   hourStem: string,    // $B$61
 *   hourBranch: string,  // $B$62
 * }} pillars
 * @returns {{
 *   B85: string, C85: string, D85: string, E85: string,
 *   B86: string, C86: string, D86: string, E86: string,
 *   B87: string, C87: string, D87: string, E87: string,
 *   B88: string, C88: string, D88: string, E88: string,
 *   _provenance: Object,
 * }}
 */
export function computeSheet27B85ThroughE88(pillars) {
  if (pillars === null || typeof pillars !== "object") {
    throw new TypeError("[Batch07-2] pillars must be an object.");
  }
  const {
    dayStem,
    yearStem,
    yearBranch,
    monthStem,
    monthBranch,
    dayBranch,
    hourStem,
    hourBranch,
  } = pillars;

  // Row 85 — direct day-master × pillar stem lookups.
  const B85 = computeSheet27Row85Cell(dayStem, yearStem);
  const C85 = computeSheet27Row85Cell(dayStem, monthStem);
  const D85 = D85_DISPLAY_STATIC_CONSTANT.value; // static, not computed
  const E85 = computeSheet27Row85Cell(dayStem, hourStem);

  // Rows 86/87/88 — chained hidden-stem → 十神 lookups per column.
  const rowFor = (pos, branchByColumn) => ({
    B: computeSheet27Rows86To88Cell(dayStem, branchByColumn.yearBranch, pos),
    C: computeSheet27Rows86To88Cell(dayStem, branchByColumn.monthBranch, pos),
    D: computeSheet27Rows86To88Cell(dayStem, branchByColumn.dayBranch, pos),
    E: computeSheet27Rows86To88Cell(dayStem, branchByColumn.hourBranch, pos),
  });
  const branches = { yearBranch, monthBranch, dayBranch, hourBranch };
  const r86 = rowFor(POSITION_TOKEN.BEN_QI, branches);
  const r87 = rowFor(POSITION_TOKEN.ZHONG_QI, branches);
  const r88 = rowFor(POSITION_TOKEN.YU_QI, branches);

  return Object.freeze({
    B85,
    C85,
    D85,
    E85,
    B86: r86.B,
    C86: r86.C,
    D86: r86.D,
    E86: r86.E,
    B87: r87.B,
    C87: r87.C,
    D87: r87.D,
    E87: r87.E,
    B88: r88.B,
    C88: r88.C,
    D88: r88.D,
    E88: r88.E,
    _provenance: Object.freeze({
      excelSource: "Sheet27!B85:E88 (D85 = DISPLAY_STATIC_CONSTANT)",
      formulaFamilyRow85:
        "=IFERROR(VLOOKUP($B$52 & \"_\" & <pillarStem>, 'DB_十神'!$F:$I, 4, FALSE()), \"\")",
      formulaFamilyRows86To88:
        "=IFERROR(VLOOKUP($B$52 & \"_\" & IFERROR(VLOOKUP(<branch> & \"_\" & <position>, 'DB_藏干_展开'!$A:$D, 4, FALSE()), \"\"), 'DB_十神'!$F:$I, 4, FALSE()), \"\")",
      cellCountComputed: 15,
      cellCountStatic: 1,
      staticCells: ["D85"],
      referenceTables: {
        DB_十神: "M02_BATCH_03 (Sheet45, AUTHORITATIVE_DATASET_COMPLETE)",
        "DB_藏干_展开": "M02_BATCH_06 (Sheet43 COMPATIBILITY_LAYER over Sheet42 DB_藏干)",
      },
      encodingWarning:
        "Position tokens 本气/中气/余气 are consumed exclusively via " +
        "Batch 06 QI_LEI_TOKEN (audited Unicode). No Chinese literal is " +
        "introduced by this module.",
    }),
  });
}
